import cx from "clsx";
import { Collection } from "kreate-common/modules/business-types";
import ButtonPrimary from "kreate-common/modules/kreate-ui/components/ButtonPrimary";
import Divider from "kreate-common/modules/kreate-ui/components/Divider";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { useRouter } from "next/router";
import React from "react";
import { MdUpload } from "react-icons/md";

import LazyArtworkList$Profile from "../../../PageProfile/containers/SectionBody/containers/SectionArtworks/components/LazyArtworkList$Profile";
import PanelGachaMinting from "../PanelGachaMinting";

import styles from "./index.module.scss";

import { useBusyCount } from "@/modules/common-hooks/useBusyCount";
import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import { range } from "@/modules/common-utils/range";
import { EM_DASH } from "@/modules/common-utils/unicode";
import Checkbox from "@/modules/kreate-components/components/Checkbox";
import PageLayout from "@/modules/kreate-components/components/PageLayout";
import Select$ArtworkOrder, {
  ArtworkOrder,
  artworkOrderItems,
} from "@/modules/kreate-components/components/Select$ArtworkOrder";
import Select$MintStatus, {
  MintStatus,
} from "@/modules/kreate-components/components/Select$MintStatus";

const ITEMS_PER_PAGE = 12;

type Props = {
  className?: string;
  style?: React.CSSProperties;
  collection?: Collection;
};

export default function TabAllArtworks({
  className,
  style,
  collection,
}: Props) {
  const loginStatus = useLoginStatus();
  const router = useRouter();

  const { busy, withDefaultErrorHandler } = useBusyCount();
  const parsedMintStatus = MintStatus.safeParse(router.query.mintStatus);
  const parsedArtworkOrder = ArtworkOrder.safeParse(router.query.order);
  const [pageCount, setPageCount] = React.useState(1);
  const [mintStatus, setMintStatus] = React.useState<MintStatus>(
    parsedMintStatus.success ? parsedMintStatus.data : "all"
  );
  const [artworkOrder, setArtworkOrder] = React.useState<ArtworkOrder>(
    parsedArtworkOrder.success ? parsedArtworkOrder.data : "newest"
  );
  const [isSingleEdition, setIsSingleEdition] = React.useState(
    router.query.isSingleEdition === "true"
  );
  const { orderBy, orderDirection } = artworkOrderItems[artworkOrder];

  React.useEffect(() => {
    setPageCount(1);
  }, [mintStatus, artworkOrder, isSingleEdition]);

  const [numFilteredArtworks, setNumFilteredArtworks] = React.useState<
    number | null | undefined
  >(null);

  const isOwner =
    loginStatus?.loggedIn && loginStatus.userId === collection?.createdBy;

  const hasNoArtworks = collection?.numArtworks === 0;

  return (
    <div className={cx(styles.container, className)} style={style}>
      <PageLayout.Section paddingTop="48px" paddingBottom="48px">
        <Flex.Row
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap="12px"
          paddingBottom="32px"
        >
          <h2 className={styles.h2}>
            {`All Artworks (${
              hasNoArtworks || numFilteredArtworks === 0
                ? "No items"
                : numFilteredArtworks === 1
                ? "1 item"
                : `${numFilteredArtworks ?? EM_DASH} items`
            })`}
          </h2>
          {!hasNoArtworks ? (
            <Flex.Row gap="16px" alignItems="center" flexWrap="wrap">
              <Checkbox
                label="1/1"
                title="Single Edition Artwork"
                value={isSingleEdition}
                onChange={withDefaultErrorHandler(async (value) => {
                  setIsSingleEdition(value);
                  const url = new URL(window.location.href);
                  value
                    ? url.searchParams.set("isSingleEdition", "true")
                    : url.searchParams.delete("isSingleEdition");
                  await router.replace(url, undefined, {
                    scroll: false,
                    shallow: true,
                  });
                })}
              />
              <Divider.Vertical className={styles.divider} />
              <Flex.Row gap="12px" flexWrap="wrap">
                <Select$MintStatus
                  value={mintStatus}
                  onChange={withDefaultErrorHandler(async (value) => {
                    setMintStatus(value);
                    const url = new URL(window.location.href);
                    url.searchParams.set("mintStatus", value);
                    await router.replace(url, undefined, {
                      scroll: false,
                      shallow: true,
                    });
                  })}
                  disabled={busy}
                />
                <Select$ArtworkOrder
                  value={artworkOrder}
                  onChange={withDefaultErrorHandler(async (value) => {
                    setArtworkOrder(value);
                    const url = new URL(window.location.href);
                    url.searchParams.set("order", value);
                    await router.replace(url, undefined, {
                      scroll: false,
                      shallow: true,
                    });
                  })}
                  disabled={busy}
                />
              </Flex.Row>
            </Flex.Row>
          ) : null}
        </Flex.Row>

        {!hasNoArtworks ? (
          <Flex.Col gap="56px" key={`${mintStatus} ${artworkOrder}`}>
            {range(pageCount).map((index) => (
              <LazyArtworkList$Profile
                key={index}
                params={
                  collection
                    ? {
                        offset: index * ITEMS_PER_PAGE,
                        limit: ITEMS_PER_PAGE,
                        orderBy,
                        orderDirection,
                        isSingleEdition: isSingleEdition || undefined,
                        mintStatus,
                        collectionId: collection.collectionId,
                      }
                    : undefined
                }
                onLoad={({ maybeHasMore, totalNumArtworks }) => {
                  if (index == 0) {
                    setNumFilteredArtworks(totalNumArtworks);
                  }
                  if (maybeHasMore) {
                    setPageCount((value) => Math.max(value, index + 2));
                  }
                }}
              />
            ))}
          </Flex.Col>
        ) : isOwner ? (
          <div>
            <ButtonPrimary
              size="xl"
              icon={<MdUpload />}
              content="Upload Artwork to This Collection"
              disabled={!collection}
              onClick={() =>
                collection &&
                router.push(
                  `/new/artwork?` +
                    new URLSearchParams({
                      initialCollectionId: collection.collectionId,
                    })
                )
              }
            />
          </div>
        ) : (
          <div className={styles.text}>{"No results found."}</div>
        )}

        {collection?.policyId &&
        collection.gachaMinting?.numRemainingTokens &&
        collection.gachaMinting.editionPrice != null ? (
          <PanelGachaMinting
            className={styles.panelGachaMinting}
            collection={collection}
          />
        ) : undefined}
      </PageLayout.Section>
    </div>
  );
}
